import { sum } from 'lodash';
import { useMemo } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppSelector } from '../../../../../app/hooks';
import {
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../../../services/data/impl/cube/cubeSlice';
import { findHighestModelVersion } from '../../../../../services/data/impl/cube/cubeUtils';
import useCurrServicesAndMeasures from '../../../../customerSummary/hooks/useCurrServicesAndMeasures';
import { selectColumnsExistInDbData } from '../../../../duckDB/duckDBSlice';
import {
  OperatorEnum,
  SCAN_DATA_TABLE_NAME,
  useScanDataQuery,
} from '../../../../scanDataApi/hooks/useScanDataQuery';
import { ScanDataEnum } from '../../../../scanDataApi/scanDataDefinitions';
import { selectCurrFeature } from '../../../../select/selectSlice';
import { BlueberrySizeDataModel } from '../types';
import { getBucketTotal } from '../utils/getBucketTotal';
// Add these imports for dynamic SQL generation
import {
  getActiveSizeBucketsForVersion,
  SizeBucket,
} from '../../../../dataMesh/blueberry/configs/blueberrySizeBucketLabelsDeprecated';
import { useBlueberryConfig } from '../../../../dataMesh/blueberry/hooks/useBlueberryConfig';
import { selectCurrServiceVersion } from '../../../../dataMesh/dataMeshSlice';
import { formatStringList } from '../../../../duckDB/utils/formatStringList';

type BlueberrySizeQueryResult = {
  [ScanDataEnum.model_name]: string;
  [ScanDataEnum.model_version]: string;
  [ScanDataEnum.class_name]: string;
  very_small: number | null;
  small: number | null;
  large: number | null;
  very_large: number | null;
  extra_large?: number | null; // Only present for v16+
  ultra_large?: number | null; // Only present for v16+
  unknown: number | null;
  dummy: null;
};

type UseBlueberrySizeDataReturnType = {
  dataset: BlueberrySizeDataModel[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
};

export const useBlueberrySizeData = (): UseBlueberrySizeDataReturnType => {
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);
  const currFeature = useAppSelector(selectCurrFeature);
  const currServiceVersion = useAppSelector(selectCurrServiceVersion);
  const blueberryConfig = useBlueberryConfig();

  const availableColumns = columnsExistInDbData?.data;
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();

  const latestService = findHighestModelVersion(
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    [FeatureEnum.blueberry]
  );

  // Get dynamic size buckets based on current version
  const activeSizeBuckets = useMemo(() => {
    if (!currServiceVersion || !blueberryConfig?.hasSizeBuckets) {
      return [];
    }
    return getActiveSizeBucketsForVersion(currServiceVersion);
  }, [currServiceVersion, blueberryConfig?.hasSizeBuckets]);

  // Dynamically build SQL arrays and column selections
  const {
    sizeBucketJsonExtractArray,
    sizeBucketColumns: sizeBucketSumColumns,
  } = useMemo(() => {
    if (activeSizeBuckets.length === 0) {
      return { sizeBucketJsonExtractArray: '', sizeBucketColumns: [] };
    }

    // Build the JSON extract array: ['very_small', 'small', 'large', ...]
    const array = formatStringList(activeSizeBuckets);

    // Build the column selections with proper indices
    const columns = activeSizeBuckets.map(
      (bucket, index) =>
        `sum(try_cast(size_data[${index + 1}] as decimal)) as ${bucket}`
    );

    return {
      sizeBucketJsonExtractArray: array,
      sizeBucketColumns: columns,
    };
  }, [activeSizeBuckets]);

  const filters = useMemo(() => {
    if (latestService != null) {
      return [
        {
          col: ScanDataEnum.model_name,
          op: OperatorEnum.eq,
          value: FeatureEnum.blueberry,
        },
        {
          col: ScanDataEnum.model_version,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelVersion],
        },
        {
          col: ScanDataEnum.class_name,
          op: OperatorEnum.in,
          value: [...BlueberryFeatures],
        },
      ];
    }
    return [];
  }, [latestService]);

  /**
   * Extract size buckets and sum over each class/model/version.
   * SQL is dynamically generated based on current service version capabilities.
   */
  const queryResult = useScanDataQuery<BlueberrySizeQueryResult>({
    disableClassFilter: true,
    cte: `WITH extracted AS (
      SELECT 
        ${ScanDataEnum.class_name},
        ${ScanDataEnum.model_name},
        ${ScanDataEnum.model_version},
        json_extract(
          ${ScanDataEnum.size_estimated_counts}, 
          ${sizeBucketJsonExtractArray}
        ) as size_data
      FROM ${SCAN_DATA_TABLE_NAME}
    )`,
    cols: [
      ScanDataEnum.class_name,
      ScanDataEnum.model_name,
      ScanDataEnum.model_version,
      ...sizeBucketSumColumns, // Dynamically generated columns
    ],
    from: 'extracted',
    group: [
      ScanDataEnum.model_name,
      ScanDataEnum.model_version,
      ScanDataEnum.class_name,
    ],
    filters: filters,
    skip:
      currFeature !== FeatureEnum.blueberry ||
      !latestService ||
      !availableColumns?.[ScanDataEnum.size_estimated_counts] ||
      !blueberryConfig?.hasSizeBuckets ||
      activeSizeBuckets.length === 0, // Skip if no size buckets available
  });

  const { dataset, totalCount } = useDeepCompareMemo(() => {
    if (!queryResult.data || activeSizeBuckets.length === 0) {
      return { dataset: [], totalCount: 0 };
    }

    const dataGreen = queryResult.data.find(
      (val) => val.class_name == FeatureEnum.blueberry_Green
    );
    const dataYellow = queryResult.data.find(
      (val) => val.class_name == FeatureEnum.blueberry_Yellow
    );
    const dataPurple = queryResult.data.find(
      (val) => val.class_name == FeatureEnum.blueberry_Purple
    );
    const dataBlue = queryResult.data.find(
      (val) => val.class_name == FeatureEnum.blueberry_Blue
    );

    // Dynamically process only the available size buckets for this version
    const fullDataset = activeSizeBuckets.map((bucketName) => {
      const green =
        (dataGreen?.[bucketName as keyof BlueberrySizeQueryResult] as
          | number
          | null) ?? 0;
      const yellow =
        (dataYellow?.[bucketName as keyof BlueberrySizeQueryResult] as
          | number
          | null) ?? 0;
      const purple =
        (dataPurple?.[bucketName as keyof BlueberrySizeQueryResult] as
          | number
          | null) ?? 0;
      const blue =
        (dataBlue?.[bucketName as keyof BlueberrySizeQueryResult] as
          | number
          | null) ?? 0;

      return {
        size: bucketName as SizeBucket, // Cast to SizeBucket type since it's a valid bucket name
        green,
        yellow,
        purple,
        blue,
        total: green + yellow + purple + blue,
      };
    });

    const bucketTotals = fullDataset.map((data) => getBucketTotal(data));
    const totalCount = sum(bucketTotals);

    // Remove 'dummy' buckets after calculating totals
    const dataset: BlueberrySizeDataModel[] = fullDataset.filter(
      (data) => data.size !== 'dummy'
    );

    return { dataset, totalCount };
  }, [queryResult.data, activeSizeBuckets]);

  return {
    dataset,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    totalCount,
  };
};
